import { useId, useMemo } from 'react';
import { beachAt } from '../lib/forecast';
import type { ForecastData } from '../lib/openMeteo';

const W = 340;
const H = 38;
/** sample the 7-day series every 3 hours — enough shape, cheap to draw */
const STEP = 3;

interface Props {
  data: ForecastData;
  beachIndex: number;
  selIdx: number;
  nowIdx: number;
}

/** 7-day effective-height strip with a cursor at the selected hour */
export default function Sparkline({ data, beachIndex, selIdx, nowIdx }: Props) {
  const gradId = useId();
  const { line, area, maxH } = useMemo(() => {
    const heights: number[] = [];
    let peak = 0.5;
    for (let t = 0; t < data.times.length; t += STEP) {
      const h = beachAt(data, beachIndex, t).effH ?? 0;
      peak = Math.max(peak, h);
      heights.push(h);
    }
    const x = (k: number) => (k / (heights.length - 1)) * W;
    const y = (h: number) => H - 2 - (h / peak) * (H - 6);
    const pts = heights.map((h, k) => `${x(k).toFixed(1)},${y(h).toFixed(1)}`).join(' L');
    return { line: `M${pts}`, area: `M0,${H} L${pts} L${W},${H} Z`, maxH: peak };
  }, [data, beachIndex]);

  const span = data.times.length - 1;
  const cx = (selIdx / span) * W;
  const nx = (nowIdx / span) * W;

  return (
    <span className="stripwrap">
      <svg
        className="swellstrip"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1B7FD4" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#1B7FD4" stopOpacity="0.04" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradId})`} />
        <path d={line} fill="none" stroke="#1B7FD4" strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
        <line
          x1={nx.toFixed(1)}
          y1={0}
          x2={nx.toFixed(1)}
          y2={H}
          stroke="#9AB0B4"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={cx.toFixed(1)}
          y1={0}
          x2={cx.toFixed(1)}
          y2={H}
          stroke="#082530"
          strokeWidth={1.4}
          strokeDasharray="2,2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {/* left edge is times[0] — today 00:00 local; the grey line is now, the dashed one the selected hour */}
      <span className="striplabel">
        <span>today</span>
        <span>peak {maxH.toFixed(1)} m</span>
        <span>+7 d</span>
      </span>
    </span>
  );
}
